const EARTH_RADIUS_METERS = 6371008.8;

function toRadians(value) {
  return (Number(value) * Math.PI) / 180;
}

function toLocalMeters(point, origin) {
  const lat = Number(point?.lat);
  const lng = Number(point?.lng);
  const originLat = Number(origin?.lat);
  const originLng = Number(origin?.lng);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    !Number.isFinite(originLat) ||
    !Number.isFinite(originLng)
  ) {
    return null;
  }

  return {
    x:
      (toRadians(lng) - toRadians(originLng)) *
      Math.cos(toRadians(originLat)) *
      EARTH_RADIUS_METERS,
    y: (toRadians(lat) - toRadians(originLat)) * EARTH_RADIUS_METERS,
  };
}

function distanceToSegmentMeters(point, segmentStart, segmentEnd) {
  const p = toLocalMeters(point, point);
  const a = toLocalMeters(segmentStart, point);
  const b = toLocalMeters(segmentEnd, point);

  if (!p || !a || !b) return Number.POSITIVE_INFINITY;

  const segmentX = b.x - a.x;
  const segmentY = b.y - a.y;
  const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;

  if (segmentLengthSquared === 0) {
    return Math.hypot(p.x - a.x, p.y - a.y);
  }

  const projection = Math.max(
    0,
    Math.min(
      1,
      ((p.x - a.x) * segmentX + (p.y - a.y) * segmentY) /
        segmentLengthSquared
    )
  );
  const closestX = a.x + projection * segmentX;
  const closestY = a.y + projection * segmentY;

  return Math.hypot(p.x - closestX, p.y - closestY);
}

function findClosestSegmentIndex(path, point) {
  if (!Array.isArray(path) || path.length < 2) return -1;

  return path.reduce(
    (best, vertex, index) => {
      const nextVertex = path[(index + 1) % path.length];
      const distance = distanceToSegmentMeters(point, vertex, nextVertex);

      return distance < best.distance ? { index, distance } : best;
    },
    { index: -1, distance: Number.POSITIVE_INFINITY }
  ).index;
}

function insertVertexOnClosestSegment(path, point, maxDistanceMeters = 25) {
  if (!Array.isArray(path) || path.length < 2) return path;

  const segmentIndex = findClosestSegmentIndex(path, point);
  if (segmentIndex < 0) return path;

  const distance = distanceToSegmentMeters(
    point,
    path[segmentIndex],
    path[(segmentIndex + 1) % path.length]
  );

  if (distance > maxDistanceMeters) return path;

  return [
    ...path.slice(0, segmentIndex + 1),
    point,
    ...path.slice(segmentIndex + 1),
  ];
}

function removeVertexAtIndex(path, index) {
  if (!Array.isArray(path) || !Number.isInteger(index)) return path;
  if (index < 0 || index >= path.length) return path;

  return path.filter((_, vertexIndex) => vertexIndex !== index);
}

function findClosestProjectedSegment(path, point, project) {
  if (!Array.isArray(path) || path.length < 2 || !point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return { index: -1, distance: Infinity };
  return path.reduce((best, vertex, index) => {
    const start = project(vertex), end = project(path[(index + 1) % path.length]);
    if (!start || !end || ![start.x, start.y, end.x, end.y].every(Number.isFinite)) return best;
    const dx = end.x - start.x, dy = end.y - start.y;
    const length = dx * dx + dy * dy;
    const ratio = length ? Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / length)) : 0;
    const distance = Math.hypot(point.x - start.x - ratio * dx, point.y - start.y - ratio * dy);
    return distance < best.distance ? { index, distance } : best;
  }, { index: -1, distance: Infinity });
}

export {
  distanceToSegmentMeters,
  findClosestSegmentIndex,
  insertVertexOnClosestSegment,
  removeVertexAtIndex,
  findClosestProjectedSegment,
};
